import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { CalendarBlank } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/** Parse `YYYY-MM-DDTHH:MM` (datetime-local) → Date | null */
export function parseDateTimeLocal(value) {
  const text = (value || "").trim();
  if (!text) return null;
  const m = text.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  const d = new Date(
    Number(m[1]), Number(m[2]) - 1, Number(m[3]),
    Number(m[4]), Number(m[5]), 0, 0,
  );
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date → `YYYY-MM-DDTHH:MM` */
export function toDateTimeLocal(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function splitTime(date) {
  if (!date) return { hour12: 12, minute: 0, ampm: "AM" };
  let h = date.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return { hour12: h, minute: date.getMinutes(), ampm };
}

function combine(day, hour12, minute, ampm) {
  const d = day ? new Date(day) : new Date();
  let h = Number(hour12) % 12;
  if (ampm === "PM") h += 12;
  d.setHours(h, Number(minute) || 0, 0, 0);
  return d;
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

function ScrollCol({ items, value, onChange, formatItem, testid }) {
  return (
    <div
      className="h-48 w-14 overflow-y-auto border border-border rounded-sm bg-background"
      data-testid={testid}
    >
      {items.map((item) => {
        const selected = item === value;
        return (
          <button
            key={item}
            type="button"
            className={cn(
              "w-full h-8 text-sm tabular-nums",
              selected
                ? "bg-primary text-primary-foreground font-semibold"
                : "hover:bg-muted text-foreground",
            )}
            onClick={() => onChange(item)}
          >
            {formatItem ? formatItem(item) : item}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Custom datetime picker with Clear + Submit (native picker has no Submit on some browsers).
 * Value/onChange use datetime-local strings: `YYYY-MM-DDTHH:MM`.
 */
export default function DateTimeLocalPicker({
  value,
  onChange,
  className,
  placeholder = "mm / dd / yyyy, -- : -- --",
  testid = "datetime-local-picker",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const parsed = useMemo(() => parseDateTimeLocal(value), [value]);
  const [draftDay, setDraftDay] = useState(parsed || new Date());
  const initialTime = splitTime(parsed || new Date());
  const [hour12, setHour12] = useState(initialTime.hour12);
  const [minute, setMinute] = useState(initialTime.minute);
  const [ampm, setAmpm] = useState(initialTime.ampm);

  useEffect(() => {
    if (!open) return;
    const base = parseDateTimeLocal(value) || new Date();
    setDraftDay(base);
    const t = splitTime(base);
    setHour12(t.hour12);
    setMinute(t.minute);
    setAmpm(t.ampm);
  }, [open, value]);

  const display = parsed
    ? format(parsed, "MM/dd/yyyy, hh:mm a")
    : placeholder;

  const submit = () => {
    const next = combine(draftDay, hour12, minute, ampm);
    onChange?.(toDateTimeLocal(next));
    setOpen(false);
  };

  const clear = () => {
    onChange?.("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-sm border border-input bg-background px-3 text-sm",
            "hover:bg-muted/40 disabled:opacity-50 disabled:pointer-events-none",
            !parsed && "text-muted-foreground",
            className,
          )}
        >
          <span className="truncate">{display}</span>
          <CalendarBlank className="w-4 h-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        data-testid={`${testid}-popover`}
      >
        <div className="flex flex-col sm:flex-row">
          <Calendar
            mode="single"
            selected={draftDay}
            onSelect={(d) => d && setDraftDay(d)}
            defaultMonth={draftDay}
          />
          <div className="flex items-start gap-1.5 p-3 border-t sm:border-t-0 sm:border-l border-border">
            <ScrollCol
              items={HOURS}
              value={hour12}
              onChange={setHour12}
              formatItem={(n) => String(n).padStart(2, "0")}
              testid={`${testid}-hours`}
            />
            <ScrollCol
              items={MINUTES}
              value={minute}
              onChange={setMinute}
              formatItem={(n) => String(n).padStart(2, "0")}
              testid={`${testid}-minutes`}
            />
            <ScrollCol
              items={["AM", "PM"]}
              value={ampm}
              onChange={setAmpm}
              testid={`${testid}-ampm`}
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-border bg-muted/30">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={clear}
            data-testid={`${testid}-clear`}
          >
            Clear
          </Button>
          <Button
            type="button"
            size="sm"
            className="rounded-sm min-w-[5.5rem]"
            onClick={submit}
            data-testid={`${testid}-submit`}
          >
            Submit
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
